import { Fragment, useState } from 'react';
import Survey from 'src/components/survey/survey';
import Modal from 'src/modules/shared/modal/components/modalNEW';
import { closeModal, showModal }  from 'src/modules/shared/modal/actions';
import ChooseUsernameForm from './chooseUsernameForm';
import FindCommunityScreen from './findCommunityScreen';
import ResearchOptInScreen from './researchOptInScreen';
import i18n from 'src/utils/translate';
import { trackFootfallEvent } from 'src/utils/footfall';
import EventType from 'src/constants/footfallEventTypes';
import { updateNotificationPreference } from 'src/modules/user/shared/actions';
import { fetchCurrentUser } from 'src/modules/shared/context/actions';
import { LinkButton } from 'styles-js/buttons';
import { useHelpScout } from 'src/utils/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'src/types';
import ModalType from 'src/constants/modalTypes';

type Props = {
  signupFlow?: SignupFlow;
  suggestedCommunities: any[];
  signupUser: ({username}: {username: string}) => Promise<void>;
  redirectPage?: () => void;
}
export enum SignupFlow {
  Community = 'Community',
  Invitation = 'Invitation',
}

export default function SignupSurvey({
  signupFlow,
  suggestedCommunities = [],
  signupUser,
  redirectPage,
}: Props) {
  const dispatch = useDispatch();

  const isSignupFromPost = useSelector(({pages}: State) => pages.singlePost.isSecondPageBlockMsgBoxVisible);

  const [currentPosition, setCurrentPosition] = useState(0);
  const [username, setUsername] = useState('');
  const [userJoinedCommunities, setUserJoinedCommunities] = useState(false);
  const [showJoinCommunityValidation, setShowJoinCommunityValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSkipFindCommunities = signupFlow === SignupFlow.Community || signupFlow === SignupFlow.Invitation;

  const onFinish = async (acceptResearch = true) => {
    trackFootfallEvent(EventType.CLICKED, {clickType: `opt-in-research-${acceptResearch ? 'yes' : 'no'}`});
    await dispatch(fetchCurrentUser());
    dispatch(updateNotificationPreference('HuResearchEmails', acceptResearch));
    setIsSubmitting(true);
    await dispatch(closeModal());

    if (redirectPage) return redirectPage();
    if (signupFlow === SignupFlow.Invitation) return;
    if (isSignupFromPost || signupFlow !== SignupFlow.Community) {
      return dispatch(showModal({modalType: ModalType.TAKE_PROFILE_SURVEY_ON_SIGNUP}));
    }
    dispatch(showModal({modalType: ModalType.TAKE_PROFILE_SURVEY_ON_SIGNUP, modalProps: {redirectOnClose: '/explore'}}));
  };

  type Screen = {
    id: number;
    key: string;
    title: string;
    content: JSX.Element;
    buttonText?: string;
  }
  const screens: Screen[] = [
    {
      id: 1,
      key: 'username',
      title: i18n.t('Create a username'),
      content: (
        <Fragment>
          <p>{i18n.t('Choose an')}{' '}<strong>{i18n.t('anonymous')}</strong>{' '}{i18n.t('username to safely share your experiences.')}</p>
          <p>{i18n.t('You can change this later.')}</p>
          <ChooseUsernameForm
            // @ts-expect-error ts-migrate(2322) FIXME: Type '{ onUsernameChange: (username: string) => vo... Remove this comment to see the full error message */}
            onUsernameChange={(username: string) => setUsername(username)}
          />
          <div className="signup-box__tips">
            {i18n.t('Tips for an anonymous username:')}
            <ul>
              <li>{i18n.t("Don't use parts of your name, address or phone number")}</li>
              <li>{i18n.t("Don't use the same username you have elsewhere")}</li>
              <li>{i18n.t('Consider your favorite hobby, animal, music, movie, color, etc')}</li>
            </ul>
          </div>
        </Fragment>
      ),
    },
    {
      id: 2,
      key: 'acceptTerms',
      title: i18n.t('Our Terms'),
      content: (
        <Fragment>
          <p>
            {i18n.t('We want you to understand how our service works. Please review')}{' '}
            <LinkButton onClick={() => {
              trackFootfallEvent(EventType.CLICKED, {clickType: 'join-our-terms'});
              useHelpScout('147-terms', { type: 'modal' });
            }}>
              {i18n.t('our terms')}
            </LinkButton>
            {' '}{i18n.t('and')}{' '}
            <LinkButton onClick={() => {
              trackFootfallEvent(EventType.CLICKED, {clickType: 'join-privacy'});
              useHelpScout('148-privacy', { type: 'modal' });
            }}>
              {i18n.t('privacy policy')}
            </LinkButton>.
          </p>
          <div className="signup-box__tips">
            <h4 className="signup-box__highlights">{i18n.t('The highlights')}</h4>
            <ul>
              <li>{i18n.t('You must be 18 or older')}</li>
              <li>{i18n.t('Your contact information is not shared with 3rd parties without your consent')}</li>
              <li>{i18n.t('Do not use the site as a substitute for professional health advice')}</li>
              <li>{i18n.t('Your contributions must comply with the laws of the country from which they are posted')}</li>
              <li>{i18n.t('We only use information about you consistent with applicable law')}</li>
            </ul>
          </div>
        </Fragment>
      ),
      buttonText: i18n.t('Next'),
    },
    {
      id: 3,
      key: 'findCommunities',
      title: i18n.t('Find your communities'),
      content: (
        <FindCommunityScreen
          setJoinedCommunities={(userJoinedCommunities: boolean) => setUserJoinedCommunities(userJoinedCommunities)}
          setJoinedCommunitiesError={(showError: boolean) => setShowJoinCommunityValidation(showError)}
          showJoinedCommunitiesValidationError={showJoinCommunityValidation}
          suggestedCommunities={suggestedCommunities}/>
      ),
      buttonText: i18n.t('Next'),
    },
    {
      id: 4,
      key: 'acceptResearch',
      title: i18n.t('One last thing...'),
      content: <ResearchOptInScreen onFinish={onFinish} />,
      buttonText: i18n.t('Yes'),
    },
  ];

  const filteredScreens = isSkipFindCommunities ? screens.filter(({key}) => key !== 'findCommunities') : screens;

  const currentScreen = filteredScreens[currentPosition];

  const progressValues = isSkipFindCommunities
    ? [50, currentPosition >= 1 ? 25 : 0, currentPosition >= 2 ? 15 : 0]
    : [40, currentPosition >= 1 ? 20 : 0, currentPosition >= 2 ? 20 : 0, currentPosition >= 3 ? 10 : 0];

  const surveyProgress = progressValues.reduce((a, b) => a + b, 0);

  const isValidScreen = () => {
    if (isSubmitting) {
      return false;
    }
    switch (currentScreen.key) {
      case 'username':
        return !!username;
      default:
        return true;
    }
  };

  const onPositionChanged = async (position: number) => {
    switch (currentScreen.key) {
      case 'username':
        trackFootfallEvent(EventType.CLICKED, {clickType: 'join-username-entered'});
        break;
      case 'acceptTerms':
        trackFootfallEvent(EventType.CLICKED, {clickType: 'join-agreed'});
        // creates user after accepting terms
        await signupUser({ username });
        break;
      case 'findCommunities':
        trackFootfallEvent(EventType.CLICKED, {clickType: 'join-find-communities'});
    }
    setCurrentPosition(position);
  };

  const renderScreen = (screen: Screen) => (
    <div className="survey__screen survey__screen--no-header signup-box__questions" key={screen.key}>
      <h3 className="signup-box__questions__title">{screen.title}</h3>
      {screen.content}
    </div>
  );

  const showFindCommunityError = () => {
    setShowJoinCommunityValidation(!userJoinedCommunities);
  };

  return (
    <Modal customHeader={true} noClose={true}>
      <Survey
        className="signup-box__survey"
        hideHeader={true}
        isNextButtonDisabled={!isValidScreen()}
        progress={surveyProgress}
        renderScreen={renderScreen}
        screens={filteredScreens}
        // @ts-expect-error ts-migrate(2322) FIXME: Type '{ className: string; hideHeader: true; isNex... Remove this comment to see the full error message
        onFinish={onFinish}
        onNext={currentScreen.key !== 'findCommunities' ? null : (userJoinedCommunities ? null : showFindCommunityError)}
        onPositionChanged={onPositionChanged}
      />
    </Modal>
  );
}

